import { useRef, useState } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { FontFamily, TextStyle } from "@tiptap/extension-text-style";
import { toast } from "sonner";
import { ChapterImage } from "@/components/ChapterImage";
import { api, formatApiError } from "@/lib/api";
import { FONTS } from "@/lib/richtext";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, ImagePlus, Link2, Heading2, Heading3, Italic, List, ListOrdered, Quote, Redo2, Sparkle, Strikethrough, Underline, Undo2 } from "lucide-react";
import { cleanPaste } from "@/lib/richtext";

const Btn = ({ on, onClick, title, icon: Icon, testId }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} aria-pressed={!!on} data-testid={`we-${testId}`}
    className={`h-8 w-8 shrink-0 rounded-md flex items-center justify-center transition-colors ${on ? "bg-amber-500/15 text-amber-300" : "text-slate-500 hover:text-slate-100 hover:bg-veil/[0.05]"}`}>
    <Icon className="w-4 h-4" />
  </button>
);
const Sep = () => <span className="shrink-0 w-px h-5 bg-line mx-1" />;

const MAX = 10 * 1024 * 1024;
export const uploadImages = async (editor, files) => {
  for (const f of files) {
    if (!/^image\/(jpeg|png|gif|webp)$/.test(f.type)) { toast.error("Use a JPG, PNG, GIF or WebP image"); continue; }
    if (f.size > MAX) { toast.error(`${f.name} is over 10 MB`); continue; }
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/writing/images", fd); editor.chain().focus().insertContent({ type: "wimage", attrs: { src: data.src } }).run(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
};
const fromUrl = async (editor) => {
  const url = window.prompt("Image or GIF link (https://…)");
  if (!url) return;
  const t = toast.loading("Copying the image into Lorely…");
  try { const { data } = await api.post("/writing/images/url", { url }); editor.chain().focus().insertContent({ type: "wimage", attrs: { src: data.src } }).run(); toast.success("Image added", { id: t }); }
  catch (e) { toast.error(formatApiError(e), { id: t }); }
};

const Toolbar = ({ editor }) => {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"), strike: e.isActive("strike"),
      h2: e.isActive("heading", { level: 2 }), h3: e.isActive("heading", { level: 3 }), quote: e.isActive("blockquote"), ul: e.isActive("bulletList"), ol: e.isActive("orderedList"),
      font: e.getAttributes("textStyle").fontFamily?.replace(/["']/g, "") || "",
      left: e.isActive({ textAlign: "left" }), center: e.isActive({ textAlign: "center" }), right: e.isActive({ textAlign: "right" }), justify: e.isActive({ textAlign: "justify" }),
    }),
  });
  const c = () => editor.chain().focus();
  const file = useRef(null);
  return (
    <div className="sticky bottom-12 z-20 flex justify-center px-3 pointer-events-none">
    <div className="pointer-events-auto flex items-center gap-0.5 max-w-full overflow-x-auto rounded-full border border-line2/80 bg-panel/85 backdrop-blur-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)] px-2 py-1" data-testid="we-toolbar">
      <select value={s.font} onChange={(e) => (e.target.value ? c().setFontFamily(e.target.value).run() : c().unsetFontFamily().run())} title="Font"
        className="shrink-0 h-8 w-32 rounded-full bg-transparent border border-line text-xs text-slate-300 px-2" data-testid="we-font">
        {FONTS.map(([l, v]) => <option key={l} value={v} style={{ fontFamily: v || undefined }}>{l === "Default" ? "Work font" : l}</option>)}
      </select>
      <Sep />
      <Btn on={s.bold} onClick={() => c().toggleBold().run()} title="Bold" icon={Bold} testId="bold" />
      <Btn on={s.italic} onClick={() => c().toggleItalic().run()} title="Italic" icon={Italic} testId="italic" />
      <Btn on={s.underline} onClick={() => c().toggleUnderline().run()} title="Underline" icon={Underline} testId="underline" />
      <Btn on={s.strike} onClick={() => c().toggleStrike().run()} title="Strikethrough" icon={Strikethrough} testId="strike" />
      <Sep />
      <Btn on={s.h2} onClick={() => c().toggleHeading({ level: 2 }).run()} title="Heading" icon={Heading2} testId="h2" />
      <Btn on={s.h3} onClick={() => c().toggleHeading({ level: 3 }).run()} title="Subheading" icon={Heading3} testId="h3" />
      <Btn on={s.quote} onClick={() => c().toggleBlockquote().run()} title="Quote" icon={Quote} testId="quote" />
      <Btn on={s.ul} onClick={() => c().toggleBulletList().run()} title="Bulleted list" icon={List} testId="ul" />
      <Btn on={s.ol} onClick={() => c().toggleOrderedList().run()} title="Numbered list" icon={ListOrdered} testId="ol" />
      <Sep />
      <Btn on={s.left} onClick={() => c().setTextAlign("left").run()} title="Align left" icon={AlignLeft} testId="align-left" />
      <Btn on={s.center} onClick={() => c().setTextAlign("center").run()} title="Centre" icon={AlignCenter} testId="align-center" />
      <Btn on={s.right} onClick={() => c().setTextAlign("right").run()} title="Align right" icon={AlignRight} testId="align-right" />
      <Btn on={s.justify} onClick={() => c().setTextAlign("justify").run()} title="Justify" icon={AlignJustify} testId="align-justify" />
      <Btn onClick={() => c().setHorizontalRule().run()} title="Scene break" icon={Sparkle} testId="scene-break" />
      <Sep />
      <Btn onClick={() => file.current.click()} title="Upload image or GIF" icon={ImagePlus} testId="image-upload" />
      <Btn onClick={() => fromUrl(editor)} title="Insert image from a link" icon={Link2} testId="image-url" />
      <input ref={file} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple hidden onChange={(e) => { uploadImages(editor, [...e.target.files]); e.target.value = ""; }} data-testid="we-image-file" />
      <Sep />
      <Btn onClick={() => c().undo().run()} title="Undo" icon={Undo2} testId="undo" />
      <Btn onClick={() => c().redo().run()} title="Redo" icon={Redo2} testId="redo" />
    </div>
    </div>
  );
};

const WIKI_OPEN = /\[\[([^[\]|]*)$/;

const Suggest = ({ s, options, pick }) => (
  <div className="fixed z-50 w-64 rounded-lg border border-amber-500/30 bg-panel shadow-xl p-1" style={{ left: Math.min(s.x, window.innerWidth - 270), top: s.y }} data-testid="we-wiki-suggest">
    {options.length === 0 && <p className="text-xs text-slate-500 px-2 py-1.5">No matching wiki page</p>}
    {options.map((p, i) => (
      <button key={p.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(p); }} data-testid={`we-wiki-option-${i}`}
        className={`w-full text-left text-sm px-2 py-1.5 rounded truncate ${i === s.idx ? "bg-amber-500/15 text-amber-200" : "text-slate-300"}`}>
        {p.title} <span className="text-[10px] text-slate-500">{p.category}</span>
      </button>
    ))}
  </div>
);

// Chapter editor; typing [[ suggests pages from the linked world and inserts a real link
export const WritingEditor = ({ value, onChange, pages, worldId, font, wrap = (x) => <div className="w-full px-6 sm:px-10 lg:px-16 py-8">{x}</div> }) => {
  const [sug, setSug] = useState(null);
  const ref = useRef({});
  const options = sug ? pages.filter((p) => p.title.toLowerCase().includes(sug.q.toLowerCase())).slice(0, 8) : [];
  ref.current.sug = sug;
  ref.current.options = options;
  const check = (e) => {
    if (!worldId) return;
    const { $from, empty } = e.state.selection;
    const m = empty && $from.parent.textBetween(Math.max(0, $from.parentOffset - 80), $from.parentOffset, undefined, "\ufffc").match(WIKI_OPEN);
    if (!m) return setSug(null);
    const at = e.view.coordsAtPos($from.pos);
    setSug((s) => ({ q: m[1], from: $from.pos - m[0].length, to: $from.pos, x: at.left, y: at.bottom + 6, idx: s?.q === m[1] ? s.idx : 0 }));
  };
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: true, autolink: false, HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" } } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }), TextStyle, FontFamily, ChapterImage,
    ],
    content: value,
    onUpdate: ({ editor: e }) => { onChange(e.isEmpty ? "" : e.getHTML(), e.getText()); check(e); },
    onSelectionUpdate: ({ editor: e }) => check(e),
    editorProps: {
      attributes: { class: "writing-prose min-h-[60vh] focus:outline-none", "data-testid": "chapter-editor" },
      transformPastedHTML: cleanPaste,
      handlePaste: (_, ev) => { const f = [...(ev.clipboardData?.files || [])].filter((x) => x.type.startsWith("image/")); if (!f.length) return false; uploadImages(ref.current.editor, f); return true; },
      handleDrop: (_, ev) => { const f = [...(ev.dataTransfer?.files || [])].filter((x) => x.type.startsWith("image/")); if (!f.length) return false; ev.preventDefault(); uploadImages(ref.current.editor, f); return true; },
      handleKeyDown: (_, ev) => {
        const { sug: s, options: o } = ref.current;
        if (!s) return false;
        if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
          const n = Math.max(o.length, 1);
          setSug((x) => ({ ...x, idx: (x.idx + (ev.key === "ArrowDown" ? 1 : -1) + n) % n }));
          return true;
        }
        if ((ev.key === "Enter" || ev.key === "Tab") && o.length) { ref.current.pick(o[s.idx] ?? o[0]); return true; }
        if (ev.key === "Escape") { setSug(null); return true; }
        return false;
      },
    },
  });
  ref.current.editor = editor;
  ref.current.pick = (p) => {
    const s = ref.current.sug;
    editor.chain().focus().insertContentAt({ from: s.from, to: s.to }, [
      { type: "text", text: p.title, marks: [{ type: "link", attrs: { href: `/worlds/${worldId}/p/${p.id}` } }] }, { type: "text", text: " " },
    ]).unsetMark("link").run();
    setSug(null);
  };
  if (!editor) return null;
  return (
    <div data-testid="writing-editor">
      {wrap(<div style={{ fontFamily: font || undefined }}><EditorContent editor={editor} /></div>)}
      <Toolbar editor={editor} />
      {sug && <Suggest s={sug} options={options} pick={ref.current.pick} />}
    </div>
  );
};
